import { cmxDateTime, objectUtils } from '@codametrix/ui-common';
import { BiomeIcon, IconName } from '@codametrix/verdigris';
import React, { useState } from 'react';
import type { ConfirmModalState, NoteEditState } from './case-notes-panel-body.types';

export type CaseNoteCardProps = {
  note: AmplifyAPI.CoderNoteRow;
  activeUser?: CMxAPI.User;
  editMode: NoteEditState;
  toggleEditMode: (toggle: boolean, noteId?: string) => void;
  onEdit: (note: AmplifyAPI.CoderNoteRow) => void;
  onDelete: (note: AmplifyAPI.CoderNoteRow) => void;
};

const noteAuthor = (note: AmplifyAPI.CoderNoteRow): string =>
  note.coder_fullName ||
  `${note.coder_firstname ?? ''} ${note.coder_lastname ?? ''}`.trim();

const noteTimestamp = (note: AmplifyAPI.CoderNoteRow): string => {
  const ts = note.modified_date || note.created_date;
  return ts ? cmxDateTime.format(ts, cmxDateTime.FORMATS.DATE_TIME_UI) : '';
};

const EMPTY_CONFIRM_MODAL: ConfirmModalState = {
  enabled: false,
  kind: 'discard',
  title: '',
  proceedLabel: '',
  dismissLabel: ''
};

export const CaseNoteCard: React.FC<CaseNoteCardProps> = ({
  note,
  activeUser,
  editMode,
  toggleEditMode,
  onEdit,
  onDelete
}) => {
  const [confirmModal, setConfirmModal] = useState<ConfirmModalState>(
    EMPTY_CONFIRM_MODAL
  );
  const [noteContent, setNoteContent] = useState(note.note_content);

  const isEditEnabled =
    editMode.isEditEnabled && editMode.noteId === note.note_id;
  const isEditedNote = !objectUtils.isEmpty(note.modified_date);
  const canManage =
    !!activeUser?.username && activeUser.username === note.coder_username;

  const handleEditClick = () => {
    setNoteContent(note.note_content);
    toggleEditMode(true, note.note_id);
  };

  const handleDeleteClick = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    setConfirmModal({
      enabled: true,
      kind: 'delete',
      title: 'Are you sure you want to delete this note?',
      dismissLabel: 'No',
      proceedLabel: 'Yes'
    });
  };

  const handleCancel = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    if (note.note_content !== noteContent) {
      setConfirmModal({
        enabled: true,
        kind: 'discard',
        title: 'Are you sure you want to discard changes?',
        dismissLabel: 'No',
        proceedLabel: 'Yes'
      });
    } else {
      toggleEditMode(false);
      setNoteContent(note.note_content);
    }
  };

  const handleSave = (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    onEdit({ ...note, note_content: noteContent });
  };

  const handleNoteContentChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setNoteContent(e.target.value);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      if (confirmModal.enabled) {
        resetConfirmModal();
      } else {
        toggleEditMode(false);
        setNoteContent(note.note_content);
      }
    }
  };

  const handleConfirmProceed = () => {
    if (confirmModal.kind === 'delete') {
      onDelete(note);
    } else {
      setNoteContent(note.note_content);
      toggleEditMode(false);
    }
    setConfirmModal(EMPTY_CONFIRM_MODAL);
  };

  const resetConfirmModal = () => {
    setConfirmModal(EMPTY_CONFIRM_MODAL);
  };

  return (
    <div onKeyDown={handleKeyDown} key={note.note_id}>
      {confirmModal.enabled ? (
        <div
          className="ca-note-item ca-note-confirm"
          data-testid={`confirm-${note.note_id}`}
        >
          <span className="ca-note-confirm__title">{confirmModal.title}</span>
          <div className="ca-compose-row">
            <button
              type="button"
              className="ca-note-cancel-btn"
              onClick={resetConfirmModal}
            >
              {confirmModal.dismissLabel}
            </button>
            <button
              type="button"
              className="ca-note-add-btn"
              onClick={handleConfirmProceed}
            >
              {confirmModal.proceedLabel}
            </button>
          </div>
        </div>
      ) : !isEditEnabled ? (
        <div className="ca-note-item" data-testid={`note-${note.note_id}`}>
          <div className="ca-note-meta">
            <span className="ca-note-author">{noteAuthor(note)}</span>
            <span className="ca-note-time">
              {noteTimestamp(note)}
              {isEditedNote ? ' | Edited' : ''}
            </span>
            {canManage && (
              <div className="ca-note-actions">
                <div
                  onClick={handleEditClick}
                  data-testid={`edit-note-${note.note_id}`}
                >
                  <BiomeIcon
                    iconName={IconName.Edit}
                    iconSize="20"
                    iconClass="ca-note-action-icon"
                    titleText="Edit note"
                  />
                </div>
                <div
                  onClick={handleDeleteClick}
                  data-testid={`delete-note-${note.note_id}`}
                >
                  <BiomeIcon
                    iconName={IconName.Trash}
                    iconSize="20"
                    iconClass="ca-note-action-icon"
                    titleText="Delete note"
                  />
                </div>
              </div>
            )}
          </div>
          <div className="ca-note-text" data-testid="note-content">
            {note.note_content}
          </div>
        </div>
      ) : (
        <div className="ca-note-item ca-note-item--editing">
          <textarea
            className="ca-note-compose__input"
            data-testid={`edit-note-textarea-${note.note_id}`}
            value={noteContent}
            onChange={handleNoteContentChange}
          />
          <div className="ca-compose-row">
            <button
              type="button"
              className="ca-note-cancel-btn"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button
              type="button"
              className="ca-note-add-btn"
              onClick={handleSave}
              disabled={objectUtils.isEmpty(noteContent)}
            >
              Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

CaseNoteCard.displayName = 'CaseNoteCard';
